import React, { ReactNode, useMemo, useState, JSX } from 'react';
import { Col } from 'reactstrap';
import { CustomFormComponentsType, FormConfig, FormElement } from '../types/types';
import {
  isDateInput,
  isDatePicker,
  isExternalElement,
  isInput,
  isInputSearchDropdown,
  isLabel,
  isPhoneInput,
  isRadioGroup,
  isSelect,
  isTextarea
} from '../types/typeGuards';
import InputWrapper from '../formComponents/InputWrapper';
import SelectWrapper from '../formComponents/SelectWrapper';
import DatePickerWrapper from '../formComponents/DatePickerWrapper';
import { faInfoCircle, faWindowClose } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TextareaWrapper from '../formComponents/TextareaWrapper';
import classNames from 'classnames';
import InputSearchDropdownWrapper from '../formComponents/InputSearchDropdownWrapper';
import RadioGroupWrapper from '../formComponents/RadioGroupWrapper';
import PhoneInputWrapper from '../formComponents/PhoneInputWrapper';

interface Props {
  element: FormElement;
  keySuffix?: FormConfig['keySuffix'];
  externalData?: Record<string, unknown>;
  children?: ReactNode | ReactNode[];
  CustomFormComponents?: CustomFormComponentsType;
}

const ElementRenderer = (props: Props): JSX.Element => {
  const { element, keySuffix, externalData, children, CustomFormComponents } = props;
  const { rowGrid, className, infoText, infoTextRowGrid, label } = element;
  const [isInfoTextDisplayed, setIsInfoTextDisplayed] = useState(false);

  const hideElement = useMemo(() => {
    return (
      element.hideOnExternalChange &&
      externalData &&
      externalData.hasOwnProperty(element.hideOnExternalChange.property) &&
      ((element.hideOnExternalChange.hideOnFalse &&
        !externalData[element.hideOnExternalChange.property]) ||
        (!element.hideOnExternalChange.hideOnFalse &&
          externalData[element.hideOnExternalChange.property]))
    );
  }, [element, externalData]);

  const handleInfoIconClick = () => {
    setIsInfoTextDisplayed(!isInfoTextDisplayed);
  };

  /* eslint-disable */
  return (
    <>
      {
        <Col
          xs={rowGrid?.xs || 12}
          sm={rowGrid?.sm || rowGrid?.xs || 12}
          md={rowGrid?.md || rowGrid?.sm || rowGrid?.xs || 12}
          lg={rowGrid?.lg || rowGrid?.md || rowGrid?.sm || rowGrid?.xs || 12}
          xl={rowGrid?.xl || rowGrid?.lg || rowGrid?.md || rowGrid?.sm || rowGrid?.xs || 12}
          xxl={
            rowGrid?.xxl ||
            rowGrid?.xl ||
            rowGrid?.lg ||
            rowGrid?.md ||
            rowGrid?.sm ||
            rowGrid?.xs ||
            12
          }
          key={element.key}
          className={classNames('element-renderer', 'mb-3', {
            [className || '']: !!className,
            'd-none': hideElement,
            'element-renderer--external': isExternalElement(element)
          })}
        >
          {isExternalElement(element) &&
          children &&
          (!Array.isArray(children) ||
            (Array.isArray(children) && children[element.childIndex])) ? (
            <>{Array.isArray(children) ? children[element.childIndex] : children}</>
          ) : isInput(element) || isDateInput(element) ? (
            <InputWrapper config={element} keySuffix={keySuffix} />
          ) : isLabel(element) ? (
            <div className={className}>{label || ''}</div>
          ) : isRadioGroup(element) ? (
            <RadioGroupWrapper config={element} keySuffix={keySuffix} externalData={externalData} />
          ) : isInputSearchDropdown(element) ? (
            <InputSearchDropdownWrapper
              config={element}
              keySuffix={keySuffix}
              externalData={externalData}
            />
          ) : isPhoneInput(element) ? (
            <PhoneInputWrapper config={element} keySuffix={keySuffix} />
          ) : isTextarea(element) ? (
            <TextareaWrapper config={element} keySuffix={keySuffix} />
          ) : isSelect(element) ? (
            <SelectWrapper config={element} keySuffix={keySuffix} externalData={externalData} />
          ) : isDatePicker(element) ? (
            !!CustomFormComponents?.['datePicker'] ? (
              (() => {
                const CustomDatePicker = CustomFormComponents['datePicker'];
                return <CustomDatePicker config={element} keySuffix={keySuffix} />;
              })()
            ) : (
              <DatePickerWrapper config={element} keySuffix={keySuffix} />
            )
          ) : null}
          {infoText && infoTextRowGrid && !isInfoTextDisplayed && (
            <FontAwesomeIcon
              className="element-renderer__icon element-renderer__icon--closed d-lg-none"
              icon={faInfoCircle}
              onClick={handleInfoIconClick}
            />
          )}
          {infoText && infoTextRowGrid && isInfoTextDisplayed && (
            <FontAwesomeIcon
              className="element-renderer__icon element-renderer__icon--open d-lg-none"
              icon={faWindowClose}
              onClick={handleInfoIconClick}
            />
          )}
        </Col>
      }
      {infoText && infoTextRowGrid && (
        <Col
          lg={
            infoTextRowGrid?.lg ||
            infoTextRowGrid?.md ||
            infoTextRowGrid?.sm ||
            infoTextRowGrid?.xs ||
            12
          }
          xl={
            infoTextRowGrid?.xl ||
            infoTextRowGrid?.lg ||
            infoTextRowGrid?.md ||
            infoTextRowGrid?.sm ||
            infoTextRowGrid?.xs ||
            12
          }
          key={infoText}
          className={`element-renderer mb-3 ${className || ''} ${
            isInfoTextDisplayed ? '' : 'd-none d-lg-flex'
          }`}
        >
          <span className="mt-3 mt-lg-0">{infoText}</span>
        </Col>
      )}
    </>
  );
  /* eslint-enable */
};

export default ElementRenderer;
